import { SWITCH_ELSE_PORT, switchCasePort } from '../Hasp/properties/v2/logic/index.js';
import { LOGIC_CONDITION_TYPE, LOGIC_SWITCH_TYPE } from '../actions/logicFlowWire.js';
import { stateBindingRanges } from '../gpio/gpioEditorModel.js';
import {
    bindingTargetError,
    findNode,
    getBindingSource,
    getBindingTargets,
    getEventActionWires,
    getEventSourceRef,
    getOutputWires,
    getSubjectWire,
    isFlowNode,
    validateLogicGraph,
} from './logicGraph.js';

const SKIPPED_EVENT_CODES = new Set(['event-no-source', 'event-no-name', 'event-unsupported', 'event-duplicate']);
// Conditions and switches the validator flags are left out, with what they run.
const SKIPPED_FLOW_CODES = new Set(['condition-incomplete', 'switch-incomplete', 'flow-loop']);

/** What a condition or switch compares (see actions/logicFlowWire.js). */
function compileSubject(graph, nodeId, context) {
    const from = getSubjectWire(graph, nodeId)?.from;
    if (from?.node !== undefined) return { kind: 'gpio', entity: findNode(graph, from.node)?.data?.entity ?? '' };
    if (from?.object !== undefined) {
        const object = context.getObject?.(from.object);
        return { kind: 'object', target: { pageId: context.pageId ?? null, objectId: object ? Number(object.haspid) : null } };
    }
    return { kind: 'event' };
}

/**
 * The entries a list of trigger wires runs, in wire order: action data, and
 * conditions and switches with their branches. `path` holds the flow nodes
 * above, so a loop in invalid data ends.
 */
function compileRun(graph, wires, context, skipped, path) {
    const entries = [];
    for (const wire of wires) {
        const node = findNode(graph, wire.to.node);
        if (!node || skipped.has(node.id) || path.has(node.id)) continue;
        if (node.kind === 'action') {
            entries.push({ ...structuredClone(node.data), id: node.id });
            continue;
        }
        if (!isFlowNode(node)) continue;
        const inner = new Set(path).add(node.id);
        const run = port => compileRun(graph, getOutputWires(graph, node.id, port), context, skipped, inner);
        const base = { id: node.id, subject: compileSubject(graph, node.id, context), onChange: Boolean(node.data.onChange) };
        if (node.kind === 'condition') {
            const then = run('then');
            if (then.length) {
                entries.push({ ...base, type: LOGIC_CONDITION_TYPE, conditions: structuredClone(node.data.conditions ?? []), then });
            }
        } else {
            const cases = (node.data.cases ?? []).map(item => ({ operator: item.operator, value: item.value, then: run(switchCasePort(item.id)) }));
            const otherwise = run(SWITCH_ELSE_PORT);
            if (cases.some(item => item.then.length) || otherwise.length) {
                entries.push({ ...base, type: LOGIC_SWITCH_TYPE, property: node.data.property, cases, else: otherwise });
            }
        }
    }
    return entries;
}

/**
 * Compile a page logic graph into the Designer's per-object handler format
 * `[{ id, event, actions: Action[] }]` — the input of serializeObjectActions.
 *
 * Determinism: handlers follow the order of event nodes in `graph.nodes`;
 * actions follow their wire `order`. Nodes the validator flags (no source, no
 * event, unsupported or duplicate event, no actions, incomplete condition or
 * switch) are skipped, never exported. Conditions and switches become nested
 * entries (actions/logicFlowWire.js); `context.pageId` names the page of the
 * objects they compare.
 *
 * Events of GPIO entity nodes are kept apart, per entity id: they export to
 * the page's `gpio_actions`, not to an object's `action`.
 *
 * GPIO binding nodes compile to `stateBindings`: `gpio_bindings.state`
 * entries per target object.
 *
 * @returns {{ handlers: Map<string, object[]>, gpioHandlers: Map<string, object[]>, stateBindings: Map<string, object[]>, diagnostics: object[] }}
 */
export function compileLogicGraph(graph, context = {}) {
    const diagnostics = validateLogicGraph(graph, context);
    const skipped = new Set(diagnostics
        .filter(item => SKIPPED_EVENT_CODES.has(item.code) || SKIPPED_FLOW_CODES.has(item.code))
        .map(item => item.nodeId));
    const handlers = new Map();
    const gpioHandlers = new Map();
    for (const node of graph.nodes) {
        if (node.kind !== 'event' || skipped.has(node.id)) continue;
        const actions = compileRun(graph, getEventActionWires(graph, node.id), context, skipped, new Set());
        if (actions.length === 0) continue;
        const ref = getEventSourceRef(graph, node.id);
        const handler = { id: node.id, event: node.data.event, actions };
        if (ref.object !== undefined) {
            if (!handlers.has(ref.object)) handlers.set(ref.object, []);
            handlers.get(ref.object).push(handler);
        } else {
            // Several GPIO nodes may stand for one entity; their handlers merge.
            const entity = findNode(graph, ref.node).data.entity;
            gpioHandlers.set(entity, mergeObjectActionHandlers(gpioHandlers.get(entity), [handler]));
        }
    }
    return { handlers, gpioHandlers, stateBindings: compileStateBindings(graph, context), diagnostics };
}

const RANGE_KEYS = ['in_min', 'in_max', 'out_min', 'out_max'];

/**
 * GPIO binding nodes as `gpio_bindings.state` entries per target object
 * (uuid), in node then wire order. Incomplete bindings and targets the
 * binding cannot drive are left out (validateLogicGraph warns about them).
 */
function compileStateBindings(graph, context) {
    const result = new Map();
    for (const node of graph.nodes) {
        if (node.kind !== 'binding') continue;
        const entity = getBindingSource(graph, node.id)?.data?.entity;
        const { property, target_property } = node.data ?? {};
        if (!entity || !property || !target_property) continue;
        const ranges = Object.fromEntries(RANGE_KEYS
            .filter(key => Number.isFinite(node.data[key]))
            .map(key => [key, node.data[key]]));
        for (const uuid of getBindingTargets(graph, node.id)) {
            const object = context.getObject?.(uuid);
            if (!object || bindingTargetError(node.data, object)) continue;
            // Without an authored mapping a state or brightness gets the default
            // one (the firmware needs it for state → slider/arc val); a sensor
            // value keeps the firmware default, its range is the entity's.
            const mapping = Object.keys(ranges).length > 0 || property === 'value'
                ? ranges : stateBindingRanges(object, null, property, target_property);
            if (!result.has(uuid)) result.set(uuid, []);
            result.get(uuid).push({ entity, property, target_property, ...mapping });
        }
    }
    return result;
}

/**
 * Append compiled handlers to an object's stored handlers. A compiled event
 * that already has a stored handler extends it instead of creating a
 * duplicate handler. Neither input is mutated.
 */
export function mergeObjectActionHandlers(stored = [], compiled = []) {
    const result = (stored ?? []).map(handler => ({ ...handler, actions: [...(handler.actions ?? [])] }));
    for (const handler of compiled ?? []) {
        const existing = result.find(item => item.event === handler.event);
        if (existing) existing.actions.push(...handler.actions);
        else result.push({ ...handler, actions: [...handler.actions] });
    }
    return result;
}
